import * as React from "react";
import { Provider } from "react-redux";
import { HashRouter as Router, Route, Link, Switch } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { GOOGLE_LOGIN_CLIENT_ID, IS_DEV } from "@config/consts";
import { ROUTES } from "@config/work.routing";
import { defaultValues } from "@config/work.defaultValues";
import { APP } from "@config/testIds/app";

import DefaultTheme from "@ui/themes/default";
import { Container } from "@ui/Atoms/Grid";
import { ErrorBoundary, TestWrapper } from "@ui/Atoms";
import { ErrorFallback } from "@ui/Molecules";

import { useTranslate } from "@hooks/useTranslate";
import { BreakpointProvider } from "@hooks/useBreakpoint";
import { PrismicProvider } from "@hooks/usePrismic";

import { LanguageDetector } from "@work/containers/LanguageDetector";
import {
  DefaultLayoutWork,
  LocalStoragePurger,
  RedirectToLandingPage,
} from "@work/containers";
import { Booking, Apply, PaymentProcessed, NotFound } from "@work/pages";
import store from "@work/redux/store";

const AppComponent = (): React.ReactElement => {
  const translate = useTranslate();

  return (
    <ThemeProvider theme={DefaultTheme}>
      <ErrorBoundary
        fallbackUi={() => (
          <ErrorFallback
            somethingWentWrongText={translate(
              "batwork_pwa_generic.something_went_wrong",
            )}
            appCrashExplanationText={translate(
              "batwork_pwa_generic.app_crash_explanation",
            )}
          />
        )}
      >
        <Provider store={store}>
          <BreakpointProvider>
            <LanguageDetector>
              <PrismicProvider>
                <GoogleOAuthProvider clientId={String(GOOGLE_LOGIN_CLIENT_ID)}>
                  <Router>
                    <React.Fragment>
                      <LocalStoragePurger />
                      <DefaultLayoutWork>
                        <Switch>
                          <Route
                            exact
                            path={ROUTES.BASE}
                            render={() => (
                              <React.Fragment>
                                {IS_DEV ? (
                                  <Container>
                                    <h1>Batwork:</h1>
                                    <ul>
                                      <li>
                                        <Link
                                          to={`${ROUTES.BOOKING}?serviceLocationId=${defaultValues.booking.serviceLocationId}`}
                                        >
                                          Booking
                                        </Link>
                                      </li>
                                      <li>
                                        <TestWrapper testId={APP.APPLY}>
                                          <Link to={`${ROUTES.APPLY}`}>
                                            HR Application
                                          </Link>
                                        </TestWrapper>
                                      </li>
                                    </ul>
                                  </Container>
                                ) : (
                                  <RedirectToLandingPage />
                                )}
                              </React.Fragment>
                            )}
                          />
                          <Route
                            path={ROUTES.BOOKING}
                            render={props => <Booking {...(props as any)} />}
                          />
                          <Route path={ROUTES.APPLY} component={Apply} />
                          <Route
                            path={ROUTES.PAYMENT_PROCESSED}
                            component={PaymentProcessed}
                          />
                          <Route component={NotFound} />
                        </Switch>
                      </DefaultLayoutWork>
                    </React.Fragment>
                  </Router>
                </GoogleOAuthProvider>
              </PrismicProvider>
            </LanguageDetector>
          </BreakpointProvider>
        </Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export { AppComponent as App };
